import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicTags, type Tag } from '../../shared/api/tags';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { getI18nStrings, type I18nStrings } from '../../shared/data/i18n';
import './TagWidget.css';

const TAGS_PER_PAGE = 5;
const DISPLAY_LIMIT = 50;
const SWIPE_THRESHOLD_PX = 50;
const AUTO_ADVANCE_MS = 10000;

function chunk<T>(arr: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    pages.push(arr.slice(i, i + size));
  }
  return pages;
}

/**
 * タグ一覧ウィジェット
 * Home サイドバーに表示。1ページ5件、残りはカルーセルで次画面に表示。usageCount 降順。
 */
export const TagWidget = () => {
  const lang = useLanguage();
  const i18n: I18nStrings = getI18nStrings(lang);
  const navigate = useNavigate();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pages = useMemo(() => chunk(tags, TAGS_PER_PAGE), [tags]);
  const pageCount = pages.length;
  const slideCount = pageCount > 1 ? pageCount + 2 : pageCount;
  const [slideIndex, setSlideIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const didSwipeRef = useRef(false);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPageCountRef = useRef(pageCount);

  const goPrev = useCallback(() => {
    if (pageCount <= 1) return;
    setSlideIndex((i) => (i > 1 ? i - 1 : 0));
  }, [pageCount]);

  const goNext = useCallback(() => {
    if (pageCount <= 1) return;
    setSlideIndex((i) => (i < pageCount ? i + 1 : pageCount + 1));
  }, [pageCount]);

  const handleTrackTransitionEnd = useCallback(() => {
    if (pageCount <= 1) return;
    if (slideIndex === 0) {
      setTransitionEnabled(false);
      setSlideIndex(pageCount);
    } else if (slideIndex === pageCount + 1) {
      setTransitionEnabled(false);
      setSlideIndex(1);
    }
  }, [slideIndex, pageCount]);

  useEffect(() => {
    if (!transitionEnabled) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
      return () => cancelAnimationFrame(id);
    }
  }, [transitionEnabled]);

  useEffect(() => {
    if (pageCount > 1 && prevPageCountRef.current <= 1) {
      setSlideIndex(1);
    }
    prevPageCountRef.current = pageCount;
  }, [pageCount]);

  useEffect(() => {
    if (pageCount > 0 && slideCount > 0 && slideIndex >= slideCount) {
      setSlideIndex(1);
    }
  }, [pageCount, slideCount, slideIndex]);

  const startAutoAdvanceTimer = useCallback(() => {
    if (pageCount <= 1) return;
    if (autoAdvanceTimerRef.current) clearInterval(autoAdvanceTimerRef.current);
    autoAdvanceTimerRef.current = setInterval(() => {
      goNext();
    }, AUTO_ADVANCE_MS);
  }, [pageCount, goNext]);

  const stopAutoAdvanceTimer = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearInterval(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (pageCount > 1) startAutoAdvanceTimer();
    return () => stopAutoAdvanceTimer();
  }, [pageCount, startAutoAdvanceTimer, stopAutoAdvanceTimer]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    didSwipeRef.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || pageCount <= 1) return;
      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
      didSwipeRef.current = true;
      if (deltaX > 0) goPrev();
      else goNext();
      startAutoAdvanceTimer();
    },
    [pageCount, goPrev, goNext, startAutoAdvanceTimer]
  );

  const handleTagClick = useCallback(
    (slug: string, e: React.MouseEvent) => {
      if (didSwipeRef.current) {
        e.preventDefault();
        didSwipeRef.current = false;
        return;
      }
      navigate(`/${lang}?tag=${encodeURIComponent(slug)}`);
    },
    [lang, navigate]
  );

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getPublicTags();
        const visible = (res.tags || []).filter((t) => t.isVisible);
        const sorted = [...visible].sort((a, b) => b.usageCount - a.usageCount);
        setTags(sorted.slice(0, DISPLAY_LIMIT));
      } catch (err) {
        console.warn('Failed to fetch tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, []);

  const slides = useMemo(
    () =>
      pageCount > 1 ? [pages[pageCount - 1], ...pages, pages[0]] : pages,
    [pages, pageCount]
  );

  const getRealPageIndex = useCallback(
    (index: number) => (index === 0 ? pageCount - 1 : (index - 1) % pageCount),
    [pageCount]
  );

  const getDisplayName = (tag: Tag) => (lang === 'en' && tag.nameEn ? tag.nameEn : tag.name);

  if (loading) {
    return (
      <aside className="tag-widget">
        <div className="tag-widget__content">
          <h2 className="tag-widget__title">{i18n.home.tagListTitle}</h2>
          <div className="tag-widget__loading">{i18n.common.loading}</div>
        </div>
      </aside>
    );
  }

  if (error) {
    return (
      <aside className="tag-widget">
        <div className="tag-widget__content">
          <h2 className="tag-widget__title">{i18n.home.tagListTitle}</h2>
          <div className="tag-widget__error">{i18n.home.tagListError}</div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="tag-widget">
      <div className="tag-widget__content">
        <div className="tag-widget__header">
          <h2 className="tag-widget__title">{i18n.home.tagListTitle}</h2>
          {pageCount > 1 && (
            <nav className="tag-widget__nav tag-widget__nav--desktop" aria-label={i18n.home.tagListTitle}>
              <button
                type="button"
                className="tag-widget__nav-btn"
                onClick={() => {
                  goPrev();
                  startAutoAdvanceTimer();
                }}
                aria-label={i18n.home.prevPage}
              >
                ‹
              </button>
              <button
                type="button"
                className="tag-widget__nav-btn"
                onClick={() => {
                  goNext();
                  startAutoAdvanceTimer();
                }}
                aria-label={i18n.home.nextPage}
              >
                ›
              </button>
            </nav>
          )}
        </div>
        {tags.length === 0 ? (
          <div className="tag-widget__empty">{i18n.home.tagListEmpty}</div>
        ) : (
          <div
            className="tag-widget__carousel"
            role="region"
            aria-label={i18n.home.tagListTitle}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="tag-widget__carousel-track"
              style={{
                width: `${slideCount * 100}%`,
                transform: `translateX(-${slideIndex * (100 / slideCount)}%)`,
                transition: transitionEnabled ? undefined : 'none',
              }}
              onTransitionEnd={handleTrackTransitionEnd}
            >
              {slides.map((pageTags, pageIndex) => (
                <div
                  key={pageIndex}
                  className="tag-widget__carousel-slide"
                  style={{ flex: `0 0 ${100 / slideCount}%` }}
                >
                  <ul className="tag-widget__list">
                    {pageTags.map((tag, i) => (
                      <li key={tag.id} className="tag-widget__item">
                        <button
                          type="button"
                          className="tag-widget__item-inner"
                          onClick={(e) => handleTagClick(tag.slug, e)}
                          aria-label={i18n.postDetail.filterByTag(getDisplayName(tag))}
                        >
                          <span className="tag-widget__rank">
                            {getRealPageIndex(pageIndex) * TAGS_PER_PAGE + i + 1}
                          </span>
                          <span className="tag-widget__name">{getDisplayName(tag)}</span>
                          <span className="tag-widget__count">{i18n.home.tagCount(tag.usageCount)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};
